import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Search, MapPin, Calendar, Users, Globe, Phone, Mail, ChevronDown, ChevronUp, List, Map as MapIcon, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PublicLogo from '../components/PublicLogo';

const API = process.env.REACT_APP_BACKEND_URL;

const isEmbedded = () => !!localStorage.getItem('token');

const FALLBACK_HEADER = 'https://images.pexels.com/photos/32881343/pexels-photo-32881343.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

// Custom club marker icon
const clubIcon = new L.DivIcon({
  className: 'custom-club-marker',
  html: `<div style="width:36px;height:36px;background:#7c3aed;border-radius:50%;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-size:16px;font-weight:bold;">B</div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36]
});

const ClubCard = ({ club }) => {
  const [expanded, setExpanded] = useState(false);
  const headerImg = club.header_image_url || FALLBACK_HEADER;
  const logoImg = club.logo_url;

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid={`club-card-${club.id || club.club_name}`}>
      <div className="relative h-36 bg-amber-100">
        <img src={headerImg.startsWith('http') ? headerImg : `${API}${headerImg}`} alt="" className="w-full h-full object-cover" onError={e => { e.target.src = FALLBACK_HEADER; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute -bottom-6 left-4">
          {logoImg ? (
            <img src={logoImg.startsWith('http') ? logoImg : `${API}${logoImg}`} alt={club.club_name} className="w-14 h-14 rounded-xl object-cover border-3 border-white shadow-md bg-white" onError={e => e.target.style.display='none'} />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-amber-600 flex items-center justify-center border-3 border-white shadow-md text-white font-bold text-lg">
              {club.club_name?.charAt(0)?.toUpperCase() || 'B'}
            </div>
          )}
        </div>
      </div>
      <div className="pt-8 px-5 pb-4">
        <h3 className="text-lg font-bold text-stone-900">{club.club_name}</h3>
        <div className="flex items-center gap-2 mt-1 text-sm text-stone-500">
          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{[club.city, club.state].filter(Boolean).join(', ') || 'Location not set'}</span>
        </div>
        {club.description && <p className="text-sm text-stone-600 mt-2 line-clamp-2">{club.description}</p>}
        {(club.meeting_day || club.meeting_time) && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Meetings</p>
            <div className="text-sm text-amber-700">
              {club.meeting_day && <span>{club.meeting_day}</span>}
              {club.meeting_time && <span> at {club.meeting_time}</span>}
              {club.meeting_frequency && <span className="text-xs text-amber-500"> ({club.meeting_frequency})</span>}
            </div>
            {club.meeting_location && <p className="text-xs text-amber-600 mt-1">{club.meeting_location}</p>}
          </div>
        )}
        <div className="flex items-center gap-4 mt-3 text-xs text-stone-400">
          {club.member_count && <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {club.member_count} members</span>}
          {club.established_year && <span>Est. {club.established_year}</span>}
        </div>
        <button onClick={() => setExpanded(!expanded)} className="w-full mt-3 text-xs font-medium text-amber-700 flex items-center justify-center gap-1 py-1.5 rounded-lg hover:bg-amber-50 transition-colors" data-testid="club-expand-btn">
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {expanded ? 'Less info' : 'Contact & details'}
        </button>
        {expanded && (
          <div className="mt-2 space-y-2 text-sm border-t border-stone-100 pt-3">
            {club.address && <p className="flex items-start gap-2 text-stone-600"><MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" /> {club.address}, {club.city} {club.state} {club.postcode}</p>}
            {club.phone && <p className="flex items-center gap-2 text-stone-600"><Phone className="h-4 w-4" /> <a href={`tel:${club.phone}`} className="hover:text-amber-700">{club.phone}</a></p>}
            {club.email && <p className="flex items-center gap-2 text-stone-600"><Mail className="h-4 w-4" /> <a href={`mailto:${club.email}`} className="hover:text-amber-700">{club.email}</a></p>}
            {club.website && <p className="flex items-center gap-2 text-stone-600"><Globe className="h-4 w-4" /> <a href={club.website} target="_blank" rel="noopener" className="hover:text-amber-700 underline">{club.website.replace(/^https?:\/\//, '')}</a></p>}
            {(club.social_facebook || club.social_instagram) && (
              <div className="flex items-center gap-3">
                {club.social_facebook && <a href={club.social_facebook} target="_blank" rel="noopener" className="text-blue-600 hover:text-blue-800 text-xs font-medium">Facebook</a>}
                {club.social_instagram && <a href={club.social_instagram} target="_blank" rel="noopener" className="text-pink-600 hover:text-pink-800 text-xs font-medium">Instagram</a>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const FindBeeClub = () => {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [mapCenter, setMapCenter] = useState([-27.47, 153.02]);
  const [regions, setRegions] = useState([]);
  const resultsRef = useRef(null);

  const fetchClubs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchText) params.set('search', searchText);
      if (stateFilter) params.set('state', stateFilter);
      const res = await fetch(`${API}/api/public/bee-clubs?${params}`);
      if (res.ok) {
        const data = await res.json();
        const fetched = data.clubs || [];
        setClubs(fetched);
        // Build dynamic region list from all clubs
        if (!stateFilter && !searchText) {
          const states = [...new Set(fetched.map(c => c.state).filter(Boolean))].sort();
          setRegions(states);
        }
        const withLoc = fetched.find(c => c.gps_lat && c.gps_lng);
        if (withLoc) setMapCenter([withLoc.gps_lat, withLoc.gps_lng]);
      }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchClubs(); }, []);

  const handleSearch = () => {
    fetchClubs();
    if (resultsRef.current) {
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    }
  };

  const clubsWithLocation = clubs.filter(c => c.gps_lat && c.gps_lng);
  const embedded = isEmbedded();

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Sticky Header — public only */}
      {!embedded && (
      <header className="backdrop-blur-xl bg-stone-50/80 border-b border-stone-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <PublicLogo size="header" />
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-stone-600 hover:text-amber-700 text-sm font-medium">Log In</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-xl px-5">
                Join Thrive
              </Button>
            </Link>
          </div>
        </div>
      </header>
      )}

      {/* Hero Section — matches Find Honey layout */}
      <section className="relative overflow-hidden bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border-b border-amber-100">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 md:pt-20 md:pb-24">
          <div className="max-w-2xl">
            <div className="flex items-start gap-6 mb-4">
              <div className="flex-1">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] text-stone-900">
                  Find a <span className="text-amber-600">Bee Club</span><br />Near You
                </h1>
              </div>
              <PublicLogo size="hero" className="hidden sm:block flex-shrink-0 mt-2" />
            </div>
            <p className="text-lg text-stone-600 leading-relaxed mb-8 max-w-xl">
              Connect with local beekeeping clubs and associations. Learn from experienced beekeepers, attend meetings, and join a community.
            </p>
          </div>

          {/* Search Bar — white card like Find Honey */}
          <div className="bg-white rounded-2xl p-4 border border-stone-200 shadow-sm max-w-2xl">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-stone-400" />
                <Input
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  placeholder="Search club name, city, state..."
                  className="pl-10 h-12 bg-stone-50 text-stone-900 border-stone-200 rounded-xl"
                  data-testid="club-search-input"
                />
              </div>
              <select
                value={stateFilter}
                onChange={e => setStateFilter(e.target.value)}
                className="h-12 bg-stone-50 border border-stone-200 text-stone-700 rounded-xl px-4 text-sm"
                data-testid="club-state-filter"
              >
                <option value="">All Regions</option>
                {regions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <Button
                onClick={handleSearch}
                className="h-12 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-xl px-6"
                data-testid="club-search-btn"
              >
                Search
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section ref={resultsRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-stone-900">
              {loading ? 'Searching...' : `${clubs.length} Bee Club${clubs.length !== 1 ? 's' : ''} Found`}
            </h2>
          </div>
          <div className="flex bg-stone-200 rounded-xl p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white shadow text-stone-900' : 'text-stone-500'}`}
              data-testid="view-list-btn"
            >
              <List className="h-4 w-4" /> List
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'map' ? 'bg-white shadow text-stone-900' : 'text-stone-500'}`}
              data-testid="view-map-btn"
            >
              <MapIcon className="h-4 w-4" /> Map
            </button>
          </div>
        </div>

        {/* Map View */}
        {viewMode === 'map' && clubsWithLocation.length > 0 ? (
          <div className="rounded-2xl overflow-hidden border border-stone-200 shadow-sm mb-8" style={{ height: '500px' }}>
            <MapContainer key={`${mapCenter[0]}-${mapCenter[1]}`} center={mapCenter} zoom={6} style={{ height: '100%', width: '100%' }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
              {clubsWithLocation.map(club => (
                <Marker key={club.id || club.club_name} position={[club.gps_lat, club.gps_lng]} icon={clubIcon}>
                  <Popup>
                    <div className="min-w-[200px]">
                      <strong className="text-purple-700">{club.club_name}</strong>
                      <p className="text-xs text-stone-500 mt-0.5">{[club.city, club.state].filter(Boolean).join(', ')}</p>
                      {club.description && <p className="text-xs text-stone-600 mt-1 line-clamp-2">{club.description}</p>}
                      {(club.meeting_day || club.meeting_time) && (
                        <p className="text-xs mt-1 font-medium text-amber-700">
                          Meets: {club.meeting_day}{club.meeting_time ? ` at ${club.meeting_time}` : ''}
                        </p>
                      )}
                      {club.member_count && <p className="text-xs text-stone-400 mt-0.5">{club.member_count} members</p>}
                      {club.phone && <p className="text-xs mt-1"><a href={`tel:${club.phone}`} className="text-amber-600">{club.phone}</a></p>}
                      {club.website && <p className="text-xs"><a href={club.website} target="_blank" rel="noopener" className="text-amber-600 underline">{club.website.replace(/^https?:\/\//, '')}</a></p>}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        ) : viewMode === 'map' ? (
          <div className="bg-stone-100 rounded-2xl p-12 text-center mb-8">
            <MapPin className="h-12 w-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">No clubs with map locations found. Clubs need GPS coordinates set to appear on the map.</p>
          </div>
        ) : null}

        {/* Club Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {clubs.map(club => <ClubCard key={club.id || club.club_name} club={club} />)}
        </div>

        {!loading && clubs.length === 0 && (
          <div className="text-center py-16">
            <Users className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-stone-700 mb-2">No bee clubs found yet</h3>
            <p className="text-stone-500 max-w-md mx-auto">
              {searchText || stateFilter ? 'Try a different search or state filter.' : 'Be the first to list your bee club!'}
            </p>
          </div>
        )}
      </section>

      {/* CTA Section — public only */}
      {!embedded && (
      <section className="bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-lg">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">
                Run a <span className="text-amber-400">Bee Club</span>?
              </h2>
              <p className="text-stone-400 leading-relaxed">
                List your club for free on Thrive. Help new beekeepers discover your community, attend meetings, and connect with local enthusiasts.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/register">
                <Button className="bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl px-8 h-12 text-base">
                  <Award className="h-5 w-5 mr-2" /> Join Thrive — It's Free
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="border-stone-600 text-stone-300 hover:bg-stone-800 rounded-xl px-6 h-12">
                  Already a member? Log in
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Embedded CTA — logged-in users */}
      {embedded && (
      <section className="bg-amber-50 border-t border-amber-200 py-12">
        <div className="max-w-3xl mx-auto text-center px-4">
          <h3 className="text-xl font-bold text-stone-900 mb-2">Represent a Bee Club?</h3>
          <p className="text-stone-600 mb-4">List your club for free. Help new beekeepers find your community.</p>
          <Link to="/my-profile">
            <Button className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl px-6 h-12">
              List Your Club Here
            </Button>
          </Link>
        </div>
      </section>
      )}

      {/* Footer — public only */}
      {!embedded && (
      <footer className="bg-stone-800 text-stone-400 text-center py-6 text-sm space-y-2">
        <p>&copy; {new Date().getFullYear()} Thrive Beekeeping. Supporting backyard beekeepers worldwide.</p>
        <p className="text-stone-500">Are you a supplier? <a href="/advertise" className="text-amber-400 hover:text-amber-300 underline">Advertise on Thrive</a> — reach thousands of beekeepers.</p>
      </footer>
      )}
    </div>
  );
};

export default FindBeeClub;
